/**
 * Night sky drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawings`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const nightSkyDrawingsPack: ToilePack = {
    slug: 'night-sky-drawings',
    category: 'illustration',
    name: 'Night sky drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'milkyway', label: 'Milky Way', subject: 'the Milky Way arching over a dark line of pines, a few bright stars with small flares'},
        {key: 'moon', label: 'Rising moon', subject: 'a full moon rising over old rooftops and chimneys, its craters drawn and thin clouds crossing it'},
        {key: 'tent', label: 'Lit tent', subject: 'a lone tent glowing on a hilltop under a sky full of stars and a thin crescent moon'},
    ],
    styles: [
        {key: 'chalk', label: 'White chalk', style: 'white chalk drawing, soft glowing stars, blended halos and fine dust-like detail', palette: 'white on black', framing: 'on a sheet of black paper, the whole sheet used, a thin black edge'},
        {key: 'gel', label: 'Gel pen', style: 'white gel pen drawing, tiny dots and hairline strokes, bright points of light on dark', palette: 'white ink on black', framing: 'on matte black paper, centered, a narrow black margin'},
        {key: 'silver', label: 'Silver pencil', style: 'silver and white pencil drawing, smooth gradients of glow fading into the dark', palette: 'silver and white on black', framing: 'on black card, the sky filling the sheet, a clean black border'},
    ],
    directions: ['aurora', 'a comet', 'a cabin'],
    directionHint: 'try: aurora',
    seo: {
        title: 'Night Sky Drawings: Milky Way, Rising Moon, Lit Tent',
        metaDescription: 'Nine night sky drawings: the Milky Way over pines, a rising moon over rooftops and a lit tent under stars, in chalk, gel pen or silver pencil.',
        h1: 'Night sky drawings: the Milky Way, a rising moon, a lit tent',
        tagline: 'Three night scenes, each drawn in white or silver on black paper.',
        intro: 'Three night scenes: the Milky Way arching over a line of pines, a full moon rising over old rooftops, and a tent glowing on a hilltop under stars. Each is drawn in white chalk, white gel pen and silver pencil on black, so the light shows against the dark in three ways. Type a different night subject or a twist, such as an aurora or a comet, and the same media carry it.',
        useCases: [
            'a dark sketch for a bedroom wall',
            'a practice sheet for drawing light on dark',
            'a night-themed page for a sketchbook',
            "a star-filled print for a child's room",
        ],
        faq: [
            {q: 'What is in this pack?', a: 'Nine drawings of three night scenes: the Milky Way over pines, a rising moon over rooftops, and a lit tent on a hill. Each scene appears in three white-on-black media.'},
            {q: 'Can I ask for a different night scene?', a: 'Yes. Type a subject or twist, such as an aurora, a comet or a cabin. A typed direction is a request, and the drawing may differ from the samples.'},
            {q: 'How do the three styles differ?', a: 'White chalk gives soft glowing stars with blended halos. White gel pen gives tiny dots and hairline strokes. Silver pencil gives smooth gradients of glow fading into the dark.'},
            {q: 'Is each drawing wide or tall?', a: 'Tall. The night sky drawings are taller than they are wide.'},
        ],
    },
}
